/**
 * PlanCard — single plan card primitive (issue #434).
 *
 * Renders one Annual/Monthly card. The Annual card gets the
 * "BEST VALUE" eyebrow + an accent stroke border + a larger
 * price; the Monthly card stays in standard border styling.
 *
 * The card is a `<label>` wrapping a `<input type="radio">` so
 * selecting the card updates the parent state via a click
 * anywhere on the card surface (per acceptance criterion #2 —
 * "Selecting a card toggles a radio underneath the visual").
 *
 * The actual `checked` value is owned by the parent so the
 * parent can drive the "Start <plan> plan" CTA + the sticky
 * purchase bar from the same selection.
 */
import type { ReactElement } from 'react';
import type { PlanCardContent, PlanCadence } from '../lib/paywall/planContent';

interface Props {
  plan: PlanCardContent;
  selected: boolean;
  onSelect: (cadence: PlanCadence) => void;
}

export function PlanCard({ plan, selected, onSelect }: Props): ReactElement {
  const isAnnual = plan.cadence === 'annual';
  return (
    <label
      className={
        'plan-card' +
        (isAnnual ? ' plan-card--annual' : ' plan-card--monthly') +
        (selected ? ' plan-card--selected' : '')
      }
      data-plan={plan.cadence}
      data-testid={`plan-card-${plan.cadence}`}
    >
      {plan.eyebrow && (
        <span className="plan-card-eyebrow">{plan.eyebrow}</span>
      )}
      <span className="plan-card-name">{plan.name}</span>
      <span className="plan-card-price">
        <span className="plan-card-price-numeric">{plan.priceLabel}</span>
        <span className="plan-card-price-cadence">{plan.cadenceLabel}</span>
      </span>
      <span className="plan-card-pitch">{plan.pitch}</span>
      <span className="plan-card-assurance">{plan.assurance}</span>
      <input
        type="radio"
        name="paywall-cadence"
        value={plan.cadence}
        checked={selected}
        onChange={() => onSelect(plan.cadence)}
        className="plan-card-radio"
        aria-label={`Select the ${plan.name} plan at ${plan.priceLabel}${plan.cadenceLabel}`}
      />
    </label>
  );
}